import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { format, isValid } from 'date-fns';
import toast from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
    useTheme,
    useMediaQuery,
    Box,
    TextField,
    Button,
    CircularProgress,
    Typography,
    IconButton,
    LinearProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    Chip,
    Tooltip,
    Autocomplete,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';
import {
    ArrowBackIosNew,
    Clear,
    CloudUpload,
    PictureAsPdf,
    Article,
    TableChart,
    Image,
    InsertDriveFile,
    Download,
    CheckCircle,
    EventNote,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT, TIMEZONE_OFFSET_MINUTES } from 'state/constants';
import api from 'state/axios';
import { capitalizeFirstLetter, parseUTCDate } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import ErrorBoundary from 'components/ErrorBoundary';
import TrainingSessionsDialog from './TrainingSessionsDialog';


/* ─── Constants ─────────────────────────────────── */
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const ACCEPTED_TYPES = {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
};

const ACCEPTED_MIME_SET = new Set(Object.keys(ACCEPTED_TYPES));

const STATUS_OPTIONS = [
    { value: 'planning', label: 'Planning' },
    { value: 'published', label: 'Published' },
    { value: 'archived', label: 'Archived' },
];

const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileType = ({ file, sx }) => {
    if (!file) return <CloudUpload sx={{ fontSize: 36, ...sx }} />;
    if (file.type === 'application/pdf')
        return <PictureAsPdf sx={{ fontSize: 36, color: '#e53935', ...sx }} />;
    if (file.type.includes('word'))
        return <Article sx={{ fontSize: 36, color: '#1565c0', ...sx }} />;
    if (file.type.includes('sheet'))
        return <TableChart sx={{ fontSize: 36, color: '#2e7d32', ...sx }} />;
    if (file.type.startsWith('image/'))
        return <Image sx={{ fontSize: 36, color: '#0277bd', ...sx }} />;
    return <InsertDriveFile sx={{ fontSize: 36, ...sx }} />;
};


/* ─── FileDropZone ────────────────────────────────────────────────── */
const FileDropZone = ({ value, onChange, error, helperText, disabled }) => {
    const theme = useTheme();
    const [rejectMsg, setRejectMsg] = useState('');

    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        accept: ACCEPTED_TYPES,
        maxSize: MAX_FILE_SIZE,
        multiple: false,
        disabled,
        onDrop: (accepted, rejected) => {
            if (accepted.length > 0) {
                setRejectMsg('');
                onChange(accepted[0]);
                return;
            }
            if (rejected.length > 0) {
                const code = rejected[0].errors[0]?.code;
                setRejectMsg(code === 'file-too-large'
                    ? 'File quá lớn (tối đa 5 MB)'
                    : 'Định dạng không hỗ trợ');
                onChange(null);
            }
        },
    });

    const hasError = error || !!rejectMsg;
    const errorMsg = (error && helperText) ? helperText : rejectMsg;

    const borderColor = (() => {
        if (hasError || isDragReject) return theme.palette.error.main;
        if (isDragActive) return theme.palette.primary.main;
        return theme.palette.divider;
    })();

    
    return (
        <Box>
            <Box
                {...getRootProps()}
                sx={{
                    border: '2px dashed',
                    borderColor,
                    borderRadius: 2,
                    p: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    bgcolor: isDragActive
                        ? theme.palette.action.selected
                        : theme.palette.background.paper,
                    transition: 'border-color 0.2s, background-color 0.2s',
                    ...(!disabled && {
                        '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: theme.palette.action.hover,
                        },
                    }),
                    minHeight: 80,
                }}
            >
                <input {...getInputProps()} />

                {value ? (
                    /* ── File selected ── */
                    <>
                        <FileType file={value} />
                        <Box flexGrow={1} minWidth={0}>
                            <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                                title={value.name}
                            >
                                {value.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatBytes(value.size)}
                            </Typography>
                        </Box>
                        <CheckCircle sx={{ color: 'success.main', flexShrink: 0 }} />
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onChange(null); }}
                            sx={{ flexShrink: 0 }}
                        >
                            <Clear fontSize="small" />
                        </IconButton>
                    </>
                ) : (
                    /* ── Empty state ── */
                    <>
                        <CloudUpload sx={{ fontSize: 36, color: 'text.secondary', flexShrink: 0 }} />
                        <Box>
                            <Typography variant="body2" color={isDragActive ? 'primary.main' : 'text.primary'}>
                                {isDragActive
                                    ? 'Drop file here …'
                                    : 'Drag & drop or click to browse file'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                PDF, DOCX, XLSX, JPEG, PNG · Max 5 MB
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>

            {/* Validation / rejection message */}
            {(hasError && errorMsg) && (
                <Typography 
                    variant="caption" 
                    color="error" 
                    sx={{ mt: 0.5, display: 'block', pl: 1.5 }}
                >
                    {errorMsg}
                </Typography>
            )}
        </Box>
    );
};


/* ─── CoursePage ──────────────────────────────────────────────────── */
const TrainingCoursePage = () => {
    useAuthCheck();
    const crewRanksData = useSelector((state) => state.crewRanks);
    const { trainingCourseId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isNew = !trainingCourseId || trainingCourseId === 'new';
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const formikRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [crewScheduleCodes, setCrewScheduleCodes] = useState([]);
    const [employeeOptions, setCrewOptions] = useState([]);
    const [enrolledEmployees, setEnrolledCrew] = useState([]);
    const [employeeSearch, setCrewSearch] = useState('');
    const [rankFilter, setRankFilter] = useState('');
    const [baseFilter, setBaseFilter] = useState('');
    const [enrolledSearch, setEnrolledSearch] = useState('');
    const [enrolledRank, setEnrolledRank] = useState('');
    const [enrolledBase, setEnrolledBase] = useState('');
    const [selectedAvailable, setSelectedAvailable] = useState(new Set());
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [allSubjects, setAllSubjects] = useState([]);
    const [selectedSubjects, setSelectedSubjects] = useState([]);
    const [applicableRanks, setApplicableRanks] = useState([]);
    const [applicableAcTypes, setApplicableAcTypes] = useState([]);
    const [acTypeOptions, setAcTypeOptions] = useState([]);
    const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);

    const fetchSessions = useCallback(async () => {
        if (isNew || !trainingCourseId) return;
        setSessionsLoading(true);
        try {
            const res = await api.get(`/trainingsessions?trainingCourseId=${trainingCourseId}&limit=500`);
            setSessions(res.data?.data || res.data || []);
        } catch {
            // non-critical, skip
        } finally {
            setSessionsLoading(false);
        }
    }, [isNew, trainingCourseId]);

    useEffect(() => { fetchSessions(); }, [fetchSessions]);

    // Refresh list when dialog closes
    useEffect(() => { if (!sessionDialogOpen) fetchSessions(); }, [sessionDialogOpen, fetchSessions]);

    const [course, setCourse] = useState({
        courseName: '',
        crewScheduleCode: '',
        description: '',
        startDate: new Date(),
        endDate: new Date(),
        startTime: null,
        endTime: null,
        status: 'published',
        filePath: '',
    });

    const availableBases = useMemo(
        () => [...new Set(employeeOptions.map((c) => c.homeBase).filter(Boolean))].sort(),
        [employeeOptions]
    );

    const enrolledBases = useMemo(
        () => [...new Set(enrolledEmployees.map((c) => c.base).filter(Boolean))].sort(),
        [enrolledEmployees]
    );

    const enrolledRanks = useMemo(
        () => [...new Set(enrolledEmployees.map((c) => c.rank).filter(Boolean))].sort((a, b) => {
            const ap = crewRanksData.find(r => r.rankCode === a)?.rankPriority ?? 999;
            const bp = crewRanksData.find(r => r.rankCode === b)?.rankPriority ?? 999;
            return ap !== bp ? ap - bp : a.localeCompare(b);
        }),
        [enrolledEmployees, crewRanksData]
    );

    const filteredEnrolled = useMemo(() => {
        return enrolledEmployees.filter((m) => {
            if (enrolledRank && m.rank !== enrolledRank) return false;
            if (enrolledBase && m.base !== enrolledBase) return false;
            if (enrolledSearch) {
                const q = enrolledSearch.toLowerCase();
                return m.employeeCode?.toLowerCase().includes(q) || m.employeeName?.toLowerCase().includes(q);
            }
            return true;
        });
    }, [enrolledEmployees, enrolledRank, enrolledBase, enrolledSearch]);

    const filteredAvailable = useMemo(() => {
        const enrolledSet = new Set(enrolledEmployees.map((e) => e.employeeCode));
        return employeeOptions.filter((c) => {
            if (enrolledSet.has(c.employeeCode)) return false;
            if (applicableRanks.length > 0 && !applicableRanks.includes(c.rank)) return false;
            if (applicableAcTypes.length > 0) {
                const empAcTypes = (c.crewQuals || []).map((q) => q.acType).filter(Boolean);
                if (!applicableAcTypes.some((t) => empAcTypes.includes(t))) return false;
            }
            if (rankFilter && c.rank !== rankFilter) return false;
            if (baseFilter && c.homeBase !== baseFilter) return false;
            if (employeeSearch) {
                const q = employeeSearch.toLowerCase();
                return c.employeeCode?.toLowerCase().includes(q) || c.employeeName?.toLowerCase().includes(q);
            }
            return true;
        });
    }, [employeeOptions, enrolledEmployees, applicableRanks, applicableAcTypes, rankFilter, baseFilter, employeeSearch]);

    const toggleEmployeeSelection = (employeeCode) => {
        setSelectedAvailable((prev) => {
            const next = new Set(prev);
            next.has(employeeCode) ? next.delete(employeeCode) : next.add(employeeCode);
            return next;
        });
    };

    const handleSelectAllFiltered = () => {
        const allSelected = filteredAvailable.length > 0 && filteredAvailable.every((c) => selectedAvailable.has(c.employeeCode));
        setSelectedAvailable((prev) => {
            const next = new Set(prev);
            if (allSelected) {
                filteredAvailable.forEach((c) => next.delete(c.employeeCode));
            } else {
                filteredAvailable.forEach((c) => next.add(c.employeeCode));
            }
            return next;
        });
    };

    const handleAddSelected = () => {
        const toAdd = employeeOptions.filter((c) => selectedAvailable.has(c.employeeCode));
        setEnrolledCrew((prev) => [
            ...prev,
            ...toAdd.map((c) => ({
                employeeCode: c.employeeCode,
                employeeName: c.employeeName || '',
                rank: c.rank || '',
                base: c.homeBase || '',
                enrolledAs: 'trainee',
            })),
        ].sort((a, b) => parseInt(a.employeeCode.match(/\d+$/)?.[0] || '0', 10) - parseInt(b.employeeCode.match(/\d+$/)?.[0] || '0', 10)));
        setSelectedAvailable(new Set());
    };

    const toDateTime = (time) => {
        if (!time) return null;
        const today = new Date();
        const [hours, minutes] = time.split(':').map(Number);
        today.setHours(hours, minutes, 0, 0);
        return new Date(today);
    };

    const initialValues = {
        courseName: course.courseName || '',
        description: course.description || '',
        crewScheduleCode: course.crewScheduleCode || '',
        startDate: course.startDate ? new Date(course.startDate).toISOString().split('T')[0] : null,
        endDate: course.endDate ? new Date(course.endDate).toISOString().split('T')[0] : null,
        startTime: toDateTime(course.startTime || '08:00'),
        endTime: toDateTime(course.endTime || '17:00'),
        status: course.status || 'published',
        file: null,
    };

    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                const requests = [
                    api.get('/crewschedulecodes'),
                    api.get('/employees/active'),
                    api.get('/trainingsubjects/active'),
                    api.get('/actypes/active'),
                ];
                if (!isNew) requests.push(api.get(`/trainingcourses/${trainingCourseId}`));

                const results = await Promise.all(requests);
                setCrewScheduleCodes(results[0].data);
                setCrewOptions((results[1].data || []).sort((a, b) => parseInt(a.employeeCode.match(/\d+$/)?.[0] || '0', 10) - parseInt(b.employeeCode.match(/\d+$/)?.[0] || '0', 10)));
                setAllSubjects(results[2].data || []);
                setAcTypeOptions((results[3].data || []).map((a) => a.acTypeCode).filter(Boolean).sort());
                if (!isNew && results[4]) {
                    const courseData = results[4].data;
                    setCourse(courseData);
                    setSelectedSubjects(courseData.subjects || []);
                    setApplicableRanks(courseData.applicableRanks || []);
                    setApplicableAcTypes(courseData.applicableAcTypes || []);
                    setEnrolledCrew((courseData.enrollments || []).map((e) => ({
                        employeeCode: e.employeeCode,
                        employeeName: e.employeeName || '',
                        rank: e.rank || '',
                        base: e.base || '',
                        enrolledAs: e.enrolledAs || 'trainee',
                    })).sort((a, b) => parseInt(a.employeeCode.match(/\d+$/)?.[0] || '0', 10) - parseInt(b.employeeCode.match(/\d+$/)?.[0] || '0', 10)));
                }
            } catch (err) {
                setError(err);
                toast.error('Failed to load data!');
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, [trainingCourseId, isNew]);

    // Apply prefill from ClassBuilder → Create Course
    useEffect(() => {
        if (!isNew) return;
        const prefill = location.state?.prefill;
        if (!prefill || !allSubjects.length) return;

        // First Monday of the target month
        const firstMondayOf = (dateStr) => {
            const d = new Date(dateStr + 'T00:00:00');
            d.setDate(1);
            d.setDate(1 + (8 - d.getDay()) % 7);
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };

        const monday = prefill.startDate ? firstMondayOf(prefill.startDate) : null;

        // Match subject → crewScheduleCode
        const matched = (prefill.subjects || [])
            .map((id) => allSubjects.find((s) => s._id === id || s === id))
            .filter(Boolean);

        const subjectCode = matched[0]?.subjectCode || '';
        const scheduleMatch = crewScheduleCodes.find(
            (c) => c.category === 'TRN' && c.code === subjectCode
        );

        setCourse((prev) => ({
            ...prev,
            ...(prefill.courseName && { courseName: prefill.courseName }),
            ...(monday && { startDate: monday, endDate: monday }),
            crewScheduleCode: scheduleMatch ? subjectCode : '',
        }));

        if (matched.length) setSelectedSubjects(matched);

        if (prefill.enrollments?.length) {
            setEnrolledCrew(
                prefill.enrollments.map((e) => ({
                    employeeCode: e.employeeCode,
                    employeeName: e.employeeName || '',
                    rank: e.rank || '',
                    base: e.base || '',
                    enrolledAs: e.enrolledAs || 'trainee',
                }))
            );
        }
    }, [allSubjects]); // eslint-disable-line react-hooks/exhaustive-deps

    const checkoutSchema = yup.object().shape({
        courseName: yup.string().required('Required').min(1, 'Must be at least 1 character'),
        description: yup.string(),
        crewScheduleCode: yup.string().oneOf(
            crewScheduleCodes.filter(item => item.category === 'TRN').map(item => item.code).concat(''),
            'Invalid crew schedule code'
        ),
        startDate: yup.date().nullable(),
        endDate: yup.date().nullable().min(yup.ref('startDate'), 'End date must be after start date'),
        startTime: yup.date().nullable().required('Start Time is required'),
        endTime: yup.date().nullable().required('End Time is required'),
        file: yup.mixed()
            .nullable()
            .test('fileSize', 'File quá lớn (tối đa 5 MB)', (v) => !v || v.size <= MAX_FILE_SIZE)
            .test('fileType', 'Định dạng không hỗ trợ', (v) => !v || ACCEPTED_MIME_SET.has(v.type)),
    });

    const handleExportToAIMS = (values) => {
        if (!values.startDate || !values.endDate || !values.startTime || !values.endTime) {
            toast.error('Course dates and times are required for export.');
            return;
        }
        if (!values.crewScheduleCode) {
            toast.error('Crew schedule code is required for export.');
            return;
        }
        if (enrolledEmployees.length === 0) {
            toast.error('No enrolled employees to export.');
            return;
        }

        const localStartMins = values.startTime.getHours() * 60 + values.startTime.getMinutes();
        const localEndMins = values.endTime.getHours() * 60 + values.endTime.getMinutes();

        let dutyMins = localEndMins - localStartMins;
        if (dutyMins <= 0) dutyMins += 1440;
        const pad2 = (n) => String(n).padStart(2, '0');
        const dutyStr = `${pad2(Math.floor(dutyMins / 60))}${pad2(dutyMins % 60)}`;

        const fmtMins = (mins) => {
            const m = ((mins % 1440) + 1440) % 1440;
            return `${pad2(Math.floor(m / 60))}${pad2(m % 60)}`;
        };
        const utcStartStr = fmtMins(localStartMins - TIMEZONE_OFFSET_MINUTES);
        const utcEndStr = fmtMins(localEndMins - TIMEZONE_OFFSET_MINUTES);

        const toLocalDate = (d) => {
            if (!d) return null;
            if (d instanceof Date) return new Date(d.getFullYear(), d.getMonth(), d.getDate());
            const [y, mo, day] = String(d).split('-').map(Number);
            return new Date(y, mo - 1, day);
        };

        const startLocal = toLocalDate(values.startDate);
        const endLocal = toLocalDate(values.endDate);
        if (!startLocal || !endLocal) { toast.error('Invalid course dates.'); return; }

        const lines = [];
        for (const emp of enrolledEmployees) {
            const cur = new Date(startLocal);
            while (cur <= endLocal) {
                const utcMs = cur.getTime() + localStartMins * 60000 - TIMEZONE_OFFSET_MINUTES * 60000;
                const utcDate = new Date(utcMs);
                const dateStr = `${pad2(utcDate.getUTCDate())}${pad2(utcDate.getUTCMonth() + 1)}${utcDate.getUTCFullYear()}`;
                lines.push(`${emp.employeeCode},${dateStr},${values.crewScheduleCode},,${dutyStr},,${utcStartStr},${utcEndStr}`);
                cur.setDate(cur.getDate() + 1);
            }
        }

        const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${values.crewScheduleCode}_${pad2(startLocal.getDate())}${pad2(startLocal.getMonth() + 1)}${startLocal.getFullYear()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            setIsSubmitting(true);
            setUploadProgress(0);

            const formData = new FormData();
            formData.append('courseName', values.courseName);
            formData.append('description', values.description);
            formData.append('crewScheduleCode', values.crewScheduleCode);
            formData.append('startDate', values.startDate ? parseUTCDate(values.startDate) : '');
            formData.append('endDate', values.endDate ? parseUTCDate(values.endDate) : '');
            formData.append('startTime', values.startTime ? format(values.startTime, 'HH:mm') : '');
            formData.append('endTime', values.endTime ? format(values.endTime, 'HH:mm') : '');
            formData.append('status', values.status);
            selectedSubjects.forEach((s) => formData.append('subjects[]', s._id || s));
            applicableRanks.forEach((r) => formData.append('applicableRanks[]', r));
            applicableAcTypes.forEach((t) => formData.append('applicableAcTypes[]', t));
            if (values.file) formData.append('file', values.file);

            const url = !isNew
                ? `/trainingcourses/update/${trainingCourseId}`
                : '/trainingcourses/new';

            const res = await api({
                method: !isNew ? 'put' : 'post',
                url,
                data: formData,
                onUploadProgress: (e) => {
                    if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
                },
            });

            const savedId = trainingCourseId || res.data?.course?._id || res.data?._id;
            if (savedId) {
                await api.patch(`/trainingcourses/enroll/${savedId}`, {
                    enrollments: enrolledEmployees.map((m) => ({
                        employeeCode: m.employeeCode,
                        employeeName: m.employeeName || '',
                        rank: m.rank || '',
                        base: m.base || '',
                        enrolledAs: m.enrolledAs || 'trainee',
                    })),
                });
            }

            // Auto-create a default session when saving a new single-subject course
            if (isNew && savedId && selectedSubjects.length === 1) {
                try {
                    await api.post('/trainingsessions/new', {
                        trainingCourseId: savedId,
                        subjects: [selectedSubjects[0]._id || selectedSubjects[0]],
                        startDate: values.startDate || null,
                        endDate:   values.endDate   || null,
                        startTime: values.startTime ? format(values.startTime, 'HH:mm') : null,
                        endTime:   values.endTime   ? format(values.endTime,   'HH:mm') : null,
                        maxCapacity: Math.max(20, enrolledEmployees.length),
                        status: 'planned',
                        enrollments: enrolledEmployees.map((m) => ({
                            employeeCode: m.employeeCode,
                            employeeName: m.employeeName || '',
                            rank:         m.rank || '',
                            base:         m.base || '',
                            enrolledAs:   m.enrolledAs || 'trainee',
                            status:       'enrolled',
                        })),
                    });
                } catch {
                    // non-critical — session creation failed silently
                }
            }

            toast.success(!isNew ? 'Training course updated.' : 'Training course created.');
            if (isNew) onSubmitProps.resetForm();
            navigate('/trainingcourses');
        } catch (error) {
            setError(error);
            toast.error(error?.response?.data?.message || 'Failed to save training course!');
        } finally {
            setIsSubmitting(false);
            setUploadProgress(0);
        }
    };

    const handleRemoveFile = async () => {
        try {
            await api.delete(`/trainingcourses/${trainingCourseId}/remove-file`);
            toast.success('File removed.');
            setCourse((prev) => ({ ...prev, filePath: '' }));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to remove file.');
        }
    };

    const calculateDuration = (startDate, endDate) => {
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            if (isValid(start) && isValid(end)) {
                return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
            }
        }
        return null;
    };

    const handleTimeChange = (field, newValue, setFieldValue) => {
        setFieldValue(field, newValue ?? null);
    };

    const handleDownload = async () => {
        try {
            const filename = course.filePath.split('/').pop() || course.filePath;
            const response = await api.get(`/uploads/courses/${filename}`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to download file!');
        }
    };

    return (
        <ErrorBoundary>
            <Box className="main-container-box">
                <FlexBetween>
                    <Header
                        title="Training Course"
                        subtitle={loading ? 'Loading...' : (!trainingCourseId || trainingCourseId === 'new') ? 'Create' : 'Update'}
                    />
                    {!loading && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Tooltip title="Export to AIMS text file" arrow>
                                <span>
                                    <StyledButton onClick={() => formikRef.current && handleExportToAIMS(formikRef.current.values)}>
                                        <Article />
                                    </StyledButton>
                                </span>
                            </Tooltip>
                            <StyledButton onClick={() => navigate('/trainingcourses')}>
                                <ArrowBackIosNew />
                            </StyledButton>
                        </Box>
                    )}
                </FlexBetween>

                {loading ? (
                    <FlexCenter height={LAYOUT_HEIGHT}>
                        <CircularProgress />
                    </FlexCenter>
                ) : error ? (
                    <FlexCenter height={LAYOUT_HEIGHT}>
                        <Typography color="error">Error: {error.message}</Typography>
                    </FlexCenter>
                ) : (
                    <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                            <Formik
                                innerRef={formikRef}
                                initialValues={initialValues}
                                enableReinitialize
                                validationSchema={checkoutSchema}
                                onSubmit={handleFormSubmit}
                            >
                                {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
                                    <form onSubmit={handleSubmit}>
                                        <Box
                                            display="grid"
                                            gap={2}
                                            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                        >
                                            <TextField
                                                fullWidth 
                                                required
                                                variant="filled"
                                                size="small" 
                                                label="Course Name"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.courseName}
                                                name="courseName"
                                                error={!!touched.courseName && !!errors.courseName}
                                                helperText={touched.courseName && errors.courseName}
                                                sx={{ gridColumn: isDesktop ? 'span 6' : 'span 12' }}
                                            />

                                            <FormControl 
                                                variant="filled" 
                                                size="small"
                                                sx={{ gridColumn: isDesktop ? 'span 6' : 'span 12' }}
                                            >
                                                <InputLabel>Crew Schedule Code</InputLabel>
                                                <Select
                                                    label="Crew Schedule Code"
                                                    name="crewScheduleCode"
                                                    value={values.crewScheduleCode}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={!!touched.crewScheduleCode && !!errors.crewScheduleCode}
                                                >
                                                    <MenuItem value=""><em>None</em></MenuItem>
                                                    {crewScheduleCodes
                                                        .filter(item => item.category === 'TRN')
                                                        .map((item) => (
                                                            <MenuItem key={item.code} value={item.code}>
                                                                {item.code} - {item.description}
                                                            </MenuItem>
                                                        ))}
                                                </Select>
                                                {touched.crewScheduleCode && errors.crewScheduleCode && (
                                                    <Typography color="error" variant="caption">
                                                        {errors.crewScheduleCode}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                                                                        
                                            {/* ── Subjects ── */}
                                            <Autocomplete
                                                multiple
                                                size="small"
                                                sx={{ gridColumn: isDesktop ? 'span 6' : 'span 12' }}
                                                options={allSubjects}
                                                getOptionLabel={(o) =>
                                                    typeof o === 'string'
                                                        ? o
                                                        : `${o.subjectCode} - ${o.subjectName}`
                                                }
                                                value={selectedSubjects}
                                                onChange={(_, v) => setSelectedSubjects(v)}
                                                isOptionEqualToValue={(o, v) => (o._id || o) === (v._id || v)}

                                                renderValue={(selected, getItemProps) =>
                                                    selected.map((opt, idx) => (
                                                        <Chip
                                                            {...getItemProps(idx)}
                                                            key={opt._id || idx}
                                                            label={opt.subjectCode}
                                                            size="small"
                                                        />
                                                    ))
                                                }

                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="filled"
                                                        label="Subjects"
                                                        placeholder={
                                                            selectedSubjects.length === 0
                                                                ? 'Select subjects for this course…'
                                                                : ''
                                                        }
                                                    />
                                                )}
                                            />

                                            <TextField
                                                fullWidth 
                                                variant="filled"
                                                size="small" 
                                                multiline 
                                                rows={1}
                                                label="Description"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.description}
                                                name="description"
                                                error={!!touched.description && !!errors.description}
                                                helperText={touched.description && errors.description}
                                                sx={{ gridColumn: isDesktop ? 'span 6' : 'span 12' }}
                                            />

                                            {/* ── Applicable Ranks ── */}
                                            <Autocomplete
                                                multiple
                                                size="small"
                                                sx={{ gridColumn: isDesktop ? 'span 6' : 'span 12' }}
                                                options={crewRanksData.map((r) => r.rankCode)}
                                                value={applicableRanks}
                                                onChange={(_, v) => setApplicableRanks(v)}
                                                renderValue={(selected, getItemProps) =>
                                                    selected.map((option, index) => (
                                                        <Chip
                                                            {...getItemProps(index)}
                                                            key={option}
                                                            label={option}
                                                            size="small"
                                                        />
                                                    ))
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="filled"
                                                        label="Applicable Ranks"
                                                        placeholder={applicableRanks.length === 0 ? 'All' : ''}
                                                    />
                                                )}
                                            />

                                            {/* ── Applicable AC Types ── */}
                                            <Autocomplete
                                                multiple
                                                size="small"
                                                sx={{ gridColumn: isDesktop ? 'span 6' : 'span 12' }}
                                                options={acTypeOptions}
                                                value={applicableAcTypes}
                                                onChange={(_, v) => setApplicableAcTypes(v)}
                                                renderValue={(selected, getItemProps) =>
                                                    selected.map((option, index) => (
                                                        <Chip
                                                            {...getItemProps(index)}
                                                            key={option}
                                                            label={option}
                                                            size="small"
                                                        />
                                                    ))
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="filled"
                                                        label="Applicable AC Types"
                                                        placeholder={applicableAcTypes.length === 0 ? 'All' : ''}
                                                    />
                                                )}
                                            />
                                            
                                            <DatePicker
                                                size="small" 
                                                label="Start Date"
                                                value={values.startDate ? new Date(values.startDate) : null}
                                                onChange={(v) => setFieldValue('startDate', v ?? null)}
                                                format="dd/MM/yyyy"
                                                sx={{ gridColumn: isDesktop ? 'span 3' : 'span 12' }}
                                                slotProps={{
                                                    field: { clearable: true, onClear: () => setFieldValue('startDate', null) },
                                                    textField: {
                                                        variant: 'filled',
                                                        size: "small",
                                                        error: !!touched.startDate && !!errors.startDate,
                                                        helperText: touched.startDate && errors.startDate,
                                                    },
                                                }}
                                            />

                                            <DatePicker
                                                size="small" 
                                                label="End Date"
                                                value={values.endDate ? new Date(values.endDate) : null}
                                                onChange={(v) => setFieldValue('endDate', v ?? null)}
                                                format="dd/MM/yyyy"
                                                sx={{ gridColumn: isDesktop ? 'span 3' : 'span 12' }}
                                                slotProps={{
                                                    field: { clearable: true, onClear: () => setFieldValue('endDate', null) },
                                                    textField: {
                                                        variant: 'filled',
                                                        size: "small",
                                                        error: !!touched.endDate && !!errors.endDate,
                                                        helperText: touched.endDate && errors.endDate,
                                                    },
                                                }}
                                            />

                                            <TimePicker                                                
                                                label="Start Time"
                                                value={values.startTime}
                                                onChange={(v) => handleTimeChange('startTime', v, setFieldValue)}
                                                views={['hours', 'minutes']}
                                                sx={{ gridColumn: isDesktop ? 'span 3' : 'span 12' }}
                                                slotProps={{
                                                    field: { clearable: true, onClear: () => setFieldValue('startTime', null) },
                                                    textField: {
                                                        variant: 'filled',
                                                        size: "small",
                                                        error: !!touched.startTime && !!errors.startTime,
                                                        helperText: touched.startTime && errors.startTime,
                                                    },
                                                }}
                                            />

                                            <TimePicker
                                                label="End Time"
                                                value={values.endTime}
                                                onChange={(v) => handleTimeChange('endTime', v, setFieldValue)}
                                                views={['hours', 'minutes']}
                                                sx={{ gridColumn: isDesktop ? 'span 3' : 'span 12' }}
                                                slotProps={{
                                                    field: { clearable: true, onClear: () => setFieldValue('endTime', null) },
                                                    textField: {
                                                        variant: 'filled',
                                                        size: "small",
                                                        error: !!touched.endTime && !!errors.endTime,
                                                        helperText: touched.endTime && errors.endTime,
                                                    },
                                                }}
                                            />

                                            <TextField
                                                fullWidth 
                                                variant="filled"
                                                size="small"
                                                label="Duration (days)"
                                                value={calculateDuration(values.startDate, values.endDate) || ''}
                                                slotProps={{ readOnly: true }}
                                                sx={{ gridColumn: isDesktop ? 'span 6' : 'span 12' }}
                                            />

                                            <FormControl 
                                                fullWidth 
                                                variant="filled" 
                                                size="small" 
                                                sx={{ gridColumn: isDesktop ? 'span 6' : 'span 12' }}
                                            >
                                                <InputLabel id="status-label">Status</InputLabel>
                                                <Select
                                                    labelId="status-label"
                                                    label="Status"
                                                    name="status"
                                                    value={values.status}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={Boolean(touched.status && errors.status)}
                                                >
                                                    {STATUS_OPTIONS.map((status) => (
                                                        <MenuItem key={status.value} value={status.value}>
                                                            {status.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {touched.status && errors.status && (
                                                    <Typography color="error" variant="caption">
                                                        {errors.status}
                                                    </Typography>
                                                )}
                                            </FormControl>

                                            {/* ── Existing file ── */}
                                            {course.filePath && (
                                                <Box
                                                    sx={{
                                                        gridColumn: 'span 12',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        p: 1,
                                                        gap: 1,
                                                        borderRadius: 2,
                                                        border: 1,
                                                        borderColor: 'divider',
                                                        bgcolor: 'background.paper',
                                                    }}
                                                >
                                                    <InsertDriveFile sx={{ color: 'text.secondary' }} />
                                                    <Typography 
                                                        variant="body2" 
                                                        noWrap 
                                                        sx={{ flexGrow: 1, minWidth: 0 }}
                                                    >
                                                        {course.filePath.split('/').pop()}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<Download />}
                                                        onClick={handleDownload}
                                                    >
                                                        Download
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        startIcon={<Clear />}
                                                        onClick={handleRemoveFile}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Box>
                                            )}

                                            {/* ── Training Sessions ── */}
                                            <Box
                                                sx={{
                                                    gridColumn: 'span 12',
                                                    border: 1,
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <FlexBetween
                                                    py={1} pr={1}
                                                    sx={{ bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
                                                >
                                                    <Typography variant="subtitle2">
                                                        Training Sessions{!isNew && sessions.length > 0 ? ` (${sessions.length})` : ''}
                                                    </Typography>                                                 
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<EventNote />}
                                                        disabled={isNew}
                                                        onClick={() => setSessionDialogOpen(true)}
                                                    >
                                                        Manage
                                                    </Button>
                                                </FlexBetween>

                                                {isNew ? (
                                                    <Box px={1.5} py={1}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Save the course first to manage sessions.
                                                        </Typography>
                                                    </Box>
                                                ) : sessionsLoading ? (
                                                    <FlexCenter py={1.5}>
                                                        <CircularProgress size={18} />
                                                    </FlexCenter>
                                                ) : sessions.length === 0 ? (
                                                    <Box px={1.5} py={1}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            No sessions yet.
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ overflowX: 'auto', maxHeight: 240 }}>
                                                        <Table size="small" stickyHeader>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell sx={{ width: 40 }}>No</TableCell>
                                                                    <TableCell>Subject</TableCell>
                                                                    <TableCell sx={{ width: 90 }}>Start</TableCell>
                                                                    <TableCell sx={{ width: 90 }}>End</TableCell>
                                                                    <TableCell sx={{ width: 120 }}>Time</TableCell>
                                                                    <TableCell>Location</TableCell>
                                                                    <TableCell sx={{ width: 80 }}>Capacity</TableCell>
                                                                    <TableCell sx={{ width: 100 }}>Status</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {sessions.map((s, i) => {
                                                                    const activeEnrolled = (s.enrollments || []).filter((e) => e.status !== 'withdrawn').length;
                                                                    return (
                                                                        <TableRow key={s._id} hover>
                                                                            <TableCell>
                                                                                <Typography variant="caption" color="text.secondary">{i + 1}</Typography>
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <Box display="flex" gap={0.5} flexWrap="wrap">
                                                                                    {(s.subjects || []).length
                                                                                        ? (s.subjects || []).map((sub) => (
                                                                                              <Chip key={sub._id || sub} label={sub.subjectCode || sub} size="small" variant="outlined" />
                                                                                          ))
                                                                                        : <Typography variant="caption" color="text.secondary">-</Typography>
                                                                                    }
                                                                                </Box>
                                                                            </TableCell>
                                                                            <TableCell>{s.startDate ? format(new Date(s.startDate), 'dd/MM/yy') : '-'}</TableCell>
                                                                            <TableCell>{s.endDate ? format(new Date(s.endDate), 'dd/MM/yy') : '-'}</TableCell>
                                                                            <TableCell>
                                                                                {s.startTime || '-'}
                                                                                {s.endTime ? ` → ${s.endTime}` : ''}
                                                                            </TableCell>
                                                                            <TableCell>{s.location || '-'}</TableCell>
                                                                            <TableCell>{activeEnrolled} / {s.maxCapacity}</TableCell>
                                                                            <TableCell>
                                                                                <Chip
                                                                                    label={capitalizeFirstLetter(s.status)}
                                                                                    size="small"
                                                                                    color={
                                                                                        s.status === 'active'    ? 'primary' :
                                                                                        s.status === 'completed' ? 'success' :
                                                                                        s.status === 'cancelled' ? 'error'   : 'default'
                                                                                    }
                                                                                    sx={{ minWidth: 76, justifyContent: 'center' }}
                                                                                />
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* ── Drag & drop upload ── */}
                                            <Box sx={{ gridColumn: 'span 12' }}>
                                                <FileDropZone
                                                    value={values.file}
                                                    onChange={(file) => setFieldValue('file', file ?? null)}
                                                    error={!!touched.file && !!errors.file}
                                                    helperText={touched.file && errors.file}
                                                    disabled={isSubmitting}
                                                />
                                            </Box>

                                            {/* ── Enrolled Crew - Transfer list ── */}
                                            <Box sx={{ gridColumn: 'span 12' }}>
                                                <Typography variant="subtitle2" mb={1}>
                                                    Enrollment
                                                </Typography>
                                                <Box
                                                    display="grid"
                                                    gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
                                                    sx={{ 
                                                        border: 1, 
                                                        borderColor: 'divider', 
                                                        borderRadius: 1, 
                                                        overflow: 'hidden', 
                                                        height: { xs: 'auto', sm: 420 } 
                                                    }}
                                                >
                                                    {/* ── Left: Available ── */}
                                                    <Box
                                                        display="flex"
                                                        flexDirection="column"
                                                        sx={{ borderRight: { xs: 0, sm: 1 }, borderBottom: { xs: 1, sm: 0 }, borderColor: 'divider', height: '100%' }}
                                                    >
                                                        {/* Header */}
                                                        <FlexBetween
                                                            px={1.5} 
                                                            py={0.75}
                                                            sx={{ bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
                                                        >
                                                            <Typography variant="caption" fontWeight="bold">
                                                                Available ({filteredAvailable.length})
                                                            </Typography>
                                                            {filteredAvailable.length > 0 && (
                                                                <Box display="flex" alignItems="center">
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={filteredAvailable.every((c) => selectedAvailable.has(c.employeeCode))}
                                                                        indeterminate={
                                                                            filteredAvailable.some((c) => selectedAvailable.has(c.employeeCode)) &&
                                                                            !filteredAvailable.every((c) => selectedAvailable.has(c.employeeCode))
                                                                        }
                                                                        onChange={handleSelectAllFiltered}
                                                                        disabled={isSubmitting}
                                                                        sx={{ py: 0 }}
                                                                    />
                                                                    <Typography 
                                                                        variant="caption" 
                                                                        color="text.secondary"
                                                                    >
                                                                        All
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </FlexBetween>

                                                        {/* Search + rank + base filter */}
                                                        <Box
                                                            p={1}
                                                            display="flex"
                                                            gap={1}
                                                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                                                        >
                                                            <TextField
                                                                size="small"
                                                                placeholder="Search name / code…"
                                                                value={employeeSearch}
                                                                onChange={(e) => setCrewSearch(e.target.value)}
                                                                sx={{ flex: 1 }}
                                                            />
                                                            <Select
                                                                size="small"
                                                                value={rankFilter}
                                                                onChange={(e) => setRankFilter(e.target.value)}
                                                                displayEmpty
                                                                sx={{ minWidth: 80 }}
                                                            >
                                                                <MenuItem value="">Rank</MenuItem>
                                                                {crewRanksData.slice()?.map((r) => (
                                                                    <MenuItem key={r} value={r.rankCode}>{r.rankCode}</MenuItem>
                                                                ))}
                                                            </Select>
                                                            <Select
                                                                size="small"
                                                                value={baseFilter}
                                                                onChange={(e) => setBaseFilter(e.target.value)}
                                                                displayEmpty
                                                                sx={{ minWidth: 80 }}
                                                            >
                                                                <MenuItem value="">Base</MenuItem>
                                                                {availableBases.map((b) => (
                                                                    <MenuItem key={b} value={b}>{b}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </Box>

                                                        {/* List */}
                                                        <Box sx={{ height: 280, overflowY: 'auto' }}>
                                                            {filteredAvailable.length === 0 ? (
                                                                <FlexCenter height="100%">
                                                                    <Typography variant="caption" color="text.secondary">No crew found</Typography>
                                                                </FlexCenter>
                                                            ) : filteredAvailable.map((c) => {
                                                                const isSelected = selectedAvailable.has(c.employeeCode);
                                                                return (
                                                                    <Box
                                                                        key={c.employeeCode}
                                                                        display="flex" alignItems="center" gap={1}
                                                                        px={1} py={0.5}
                                                                        onClick={() => toggleEmployeeSelection(c.employeeCode)}
                                                                        sx={{
                                                                            cursor: 'pointer',
                                                                            bgcolor: isSelected ? 'action.selected' : 'transparent',
                                                                            '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
                                                                            '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' },
                                                                        }}
                                                                    >
                                                                        <Checkbox size="small" checked={isSelected} disableRipple sx={{ p: 0, flexShrink: 0 }} />
                                                                        <Box minWidth={0}>
                                                                            <Typography variant="body2" fontWeight="bold">{c.employeeCode}</Typography>
                                                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                                                {c.employeeName} · {c.rank} · {c.homeBase}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>

                                                        {/* Add button */}
                                                        <Box 
                                                            p={1} 
                                                            sx={{ borderTop: 1, borderColor: 'divider' }}
                                                        >
                                                            <Button
                                                                fullWidth 
                                                                variant="contained" 
                                                                // color="secondary" 
                                                                size="small"
                                                                disabled={selectedAvailable.size === 0 || isSubmitting}
                                                                onClick={handleAddSelected}
                                                            >
                                                                Add{selectedAvailable.size > 0 ? ` (${selectedAvailable.size})` : ''} →
                                                            </Button>
                                                        </Box>
                                                    </Box>

                                                    {/* ── Right: Enrolled ── */}
                                                    <Box
                                                        display="flex"
                                                        flexDirection="column"
                                                    >
                                                        {/* Header */}
                                                        <FlexBetween
                                                            px={1.5} py={0.75}
                                                            sx={{
                                                                bgcolor: 'action.hover',
                                                                borderBottom: 1,
                                                                borderColor: 'divider',
                                                                minHeight: 40,
                                                            }}
                                                        >
                                                            <Typography variant="caption" fontWeight="bold">
                                                                Enrolled ({filteredEnrolled.length}{filteredEnrolled.length !== enrolledEmployees.length ? `/${enrolledEmployees.length}` : ''})
                                                            </Typography>
                                                            {enrolledEmployees.length > 0 && (
                                                                <Button
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => setEnrolledCrew([])}
                                                                    disabled={isSubmitting}
                                                                >
                                                                    Clear all
                                                                </Button>
                                                            )}
                                                        </FlexBetween>

                                                        {/* Search + rank + base filter */}
                                                        <Box
                                                            p={1}
                                                            display="flex"
                                                            gap={1}
                                                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                                                        >
                                                            <TextField
                                                                size="small"
                                                                placeholder="Search name / code…"
                                                                value={enrolledSearch}
                                                                onChange={(e) => setEnrolledSearch(e.target.value)}
                                                                sx={{ flex: 1 }}
                                                            />
                                                            <Select
                                                                size="small"
                                                                value={enrolledRank}
                                                                onChange={(e) => setEnrolledRank(e.target.value)}
                                                                displayEmpty
                                                                sx={{ minWidth: 80 }}
                                                            >
                                                                <MenuItem value="">All</MenuItem>
                                                                {enrolledRanks.map((r) => (
                                                                    <MenuItem key={r} value={r}>{r}</MenuItem>
                                                                ))}
                                                            </Select>
                                                            <Select
                                                                size="small"
                                                                value={enrolledBase}
                                                                onChange={(e) => setEnrolledBase(e.target.value)}
                                                                displayEmpty
                                                                sx={{ minWidth: 80 }}
                                                            >
                                                                <MenuItem value="">Base</MenuItem>
                                                                {enrolledBases.map((b) => (
                                                                    <MenuItem key={b} value={b}>{b}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </Box>

                                                        {/* List */}
                                                        <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 310 }}>
                                                            {enrolledEmployees.length === 0 ? (
                                                                <FlexCenter height="100%" minHeight={150}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        No employees enrolled yet
                                                                    </Typography>
                                                                </FlexCenter>
                                                            ) : filteredEnrolled.length === 0 ? (
                                                                <FlexCenter height="100%" minHeight={80}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        No match
                                                                    </Typography>
                                                                </FlexCenter>
                                                            ) : filteredEnrolled.map((m, i) => (
                                                                <Box
                                                                    key={m.employeeCode}
                                                                    display="flex" alignItems="center" gap={1}
                                                                    px={1.5} py={0.5}
                                                                    sx={{ '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' } }}
                                                                >
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{ minWidth: 20, textAlign: 'right', flexShrink: 0 }}
                                                                    >
                                                                        {i + 1}
                                                                    </Typography>
                                                                    <Box flexGrow={1} minWidth={0}>
                                                                        <Typography variant="body2" fontWeight="bold">
                                                                            {m.employeeCode}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                                            {m.employeeName} · {m.rank} · {m.base}
                                                                        </Typography>
                                                                    </Box>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => setEnrolledCrew((prev) => prev.filter((e) => e.employeeCode !== m.employeeCode))}
                                                                        disabled={isSubmitting}
                                                                    >
                                                                        <Clear fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            {/* ── Upload progress ── */}
                                            {isSubmitting && values.file && (
                                                <Box sx={{ gridColumn: 'span 12' }}>
                                                    <Box 
                                                        display="flex" 
                                                        justifyContent="space-between" 
                                                        mb={0.5}
                                                    >
                                                        <Typography variant="caption" color="text.secondary">
                                                            Uploading…
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {uploadProgress}%
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={uploadProgress}
                                                        sx={{ borderRadius: 1 }}
                                                    />
                                                </Box>
                                            )}

                                            <Button
                                                fullWidth
                                                type="submit"
                                                variant="contained"
                                                color="secondary"
                                                disabled={isSubmitting}
                                                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                                                sx={{ gridColumn: 'span 12', height: 42 }}
                                            >
                                                {isSubmitting
                                                    ? 'Saving…'
                                                    : (!trainingCourseId || trainingCourseId === 'new') ? 'Create' : 'Update'}
                                            </Button>
                                        </Box>
                                    </form>
                                )}
                            </Formik>

                            <TrainingSessionsDialog
                                open={sessionDialogOpen}
                                onClose={() => setSessionDialogOpen(false)}
                                courseId={trainingCourseId}
                                courseName={formikRef.current?.values?.courseName || course.courseName}
                                course={course}
                            />
                        </LocalizationProvider>
                    </Box>
                )}
            </Box>
        </ErrorBoundary>
    );
};

export default TrainingCoursePage;
